import { createContext, useContext, useState, useEffect } from 'react';
import { fetchTasksFromAPI } from 'shared/api/fetch-tasks';
import { Task, TaskPhase } from 'shared/types/task';
import { useSession } from '@clerk/clerk-react';
import { Id } from 'components/kanbanBoard/types';

type TasksContextType = {
    tasks: Task[];
    refreshTasks: () => Promise<void>;
    updateTaskPhase: (taskId: Id, newPhase: TaskPhase) => void;
};

const TasksContext = createContext<TasksContextType | undefined>(undefined);

export const useTasks = () => {
    const context = useContext(TasksContext);
    if (!context) {
        throw new Error('useTasks must be used within a TasksProvider');
    }
    return context;
};

export const TasksProvider = ({ children }: any) => {
    const [tasks, setTasks] = useState<Task[]>([]);
    const session = useSession();

    const refreshTasks = async () => {
        // fetch the tasks and update state
        if (session.isLoaded && session.isSignedIn) { // Only fetch if session is loaded and user is signed in
            const newTasks = await fetchTasksFromAPI(session.session);
            setTasks(newTasks);
        }
    };

    useEffect(() => {
        refreshTasks();
    }, [session.isLoaded, session.isSignedIn]); // Dependency array watches for changes in session load state and sign-in state

    const updateTaskPhase = (taskId: Id, newPhase: TaskPhase) => {
        const updatedTasks = tasks.map(task =>
            task.id === taskId ? { ...task, phase: newPhase } : task
        );
        setTasks(updatedTasks);
    };

    return (
        <TasksContext.Provider value={{ tasks, refreshTasks, updateTaskPhase  }}>
            {children}
        </TasksContext.Provider>
    );
};
